import type { App } from '@/models/App/Model'
import type { EnrichedApp } from '@codecannon/schemas/schemas/EnrichedApp'
import { useToast } from 'primevue/usetoast'
import { useRouter } from 'vue-router'

export function useSchemaValidation() {
	const toast = useToast()
	const router = useRouter()

	/**
	 * Validates the app schema and redirects to the appropriate view if invalid.
	 *
	 * @param app - The EnrichedApp instance to validate
	 * @param appId - The app ID used when redirecting to the details view
	 * @returns true if the schema is valid, false otherwise
	 */
	function validateSchema(app: EnrichedApp | null, appId: App['id']): boolean {
		if (!app) return false

		// Check if there's at least one non-User module
		if (app.modules.length === 1) {
			toast.add({
				severity: 'error',
				summary: 'Invalid schema',
				detail: 'App must have at least one non-User module',
				life: 5000,
			})
			// Redirect to edit tab
			router.replace({
				name: 'apps-details',
				params: { id: appId },
				query: { tab: 'edit', module: 'User' },
			})
			return false
		}

		// Check if all modules have at least one column
		for (const module of app.modules) {
			if (Object.keys(module.model.columns).length === 0) {
				toast.add({
					severity: 'error',
					summary: 'Invalid schema',
					detail: `Module ${module.name} has no columns. Each module must have at least one column`,
					life: 5000,
				})
				router.replace({
					name: 'apps-details',
					params: { id: appId },
					query: { tab: 'edit', module: module.name },
				})
				return false
			}
		}

		return true
	}

	return {
		validateSchema,
	}
}
